import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/constants/apiRoutes';
import { queryKeys } from 'src/constants/querykeys';
import { CopyShoppingListRequest, CopyShoppingListResponse } from 'src/types/favorite';
import axiosInstance from 'src/utils/axios';

async function copyShoppingList(
  request: CopyShoppingListRequest
): Promise<CopyShoppingListResponse> {
  const res = await axiosInstance.post<CopyShoppingListResponse>(api.copyShoppingList, {
    folderIds: Array.from(new Set(request.folderIds)),
    folderAddUsers: Array.from(new Set(request.folderAddUsers)),
  });
  return res.data;
}

interface UseCopyShoppingListMutationOptions {
  onSuccess?: (data: CopyShoppingListResponse, variables: CopyShoppingListRequest) => void;
  onError?: (error: unknown) => void;
}

const useCopyShoppingListMutation = ({
  onSuccess,
  onError,
}: UseCopyShoppingListMutationOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.favorite.copyShoppingList(),
    mutationFn: copyShoppingList,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.favorite.allFolders() });
      queryClient.invalidateQueries({ queryKey: queryKeys.favorite.folders() });
      // Drop cached recipients so Share/Copy reloads selected users when reopened.
      queryClient.removeQueries({ queryKey: [...queryKeys.favorite.root, 'clients'] });
      onSuccess?.(data, variables);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export default useCopyShoppingListMutation;
